import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import './AdminUsageReport.css';

const THAI_MONTHS = ['', 'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
function thaiDate(dateStr) {
    if (!dateStr) return '-';
    const [y, m, d] = String(dateStr).slice(0, 10).split('-').map(Number);
    return `${d} ${THAI_MONTHS[m]} ${y + 543}`;
}
function fmt(n) { return new Intl.NumberFormat('th-TH').format(n || 0); }

function AdminUsageReport() {
    const [days, setDays] = useState(14);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [sendingLine, setSendingLine] = useState(false);
    const [detail, setDetail] = useState(null);
    const [detailDate, setDetailDate] = useState(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/reports/usage', { params: { days } });
            setData(res.data);
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'โหลดข้อมูลไม่สำเร็จ' });
        } finally {
            setLoading(false);
        }
    }, [days]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const sendLineTest = async () => {
        setSendingLine(true);
        setMessage({ type: '', text: '' });
        try {
            const res = await api.post('/reports/usage/send-line-test');
            setMessage({ type: 'success', text: res.data.message || 'ส่งเข้า Line สำเร็จ' });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'ส่งไม่สำเร็จ' });
        } finally {
            setSendingLine(false);
        }
    };

    const viewDetail = async (date) => {
        setDetailDate(date);
        setDetail(null);
        try {
            const res = await api.get('/reports/usage-detail', { params: { date } });
            setDetail(res.data.users || []);
        } catch (err) {
            setDetail([]);
        }
    };

    const today = data?.today || {};

    const cards = [
        { key: 'unique_users', label: 'ผู้เข้าใช้ระบบวันนี้', value: today.unique_users, sub: `เข้าสู่ระบบ ${fmt(today.logins)} ครั้ง`, cls: 'users' },
        { key: 'slip_opens', label: 'เปิด/พิมพ์สลิป', value: today.slip_opens, sub: 'วันนี้', cls: 'slip' },
        { key: 'tawi_opens', label: 'เปิด/พิมพ์ 50 ทวิ', value: today.tawi_opens, sub: 'วันนี้', cls: 'tawi' },
        { key: 'emails', label: 'ส่งเข้าอีเมล', value: today.emails, sub: 'วันนี้', cls: 'email' },
        { key: 'slip_shares', label: 'แชร์สลิป', value: today.slip_shares, sub: `${fmt(today.slip_share_users)} คน วันนี้`, cls: 'share' },
        { key: 'requests', label: 'คำขอเอกสาร', value: today.requests, sub: 'วันนี้', cls: 'request' }
    ];

    return (
        <div className="admin-page">
            <div className="admin-header">
                <h1>รายงานการใช้งานระบบ</h1>
                <p>สรุปการเข้าใช้งาน การเปิด/พิมพ์เอกสาร และการส่งอีเมล รายวัน</p>
            </div>

            {message.text && (
                <div className={`alert alert-${message.type}`} style={{ marginBottom: 16 }}>{message.text}</div>
            )}

            {/* Today's summary cards */}
            <div className="usage-cards">
                {cards.map(c => (
                    <div key={c.key} className={`usage-card ${c.cls}`}>
                        <span className="usage-card-value">{fmt(c.value)}</span>
                        <span className="usage-card-label">{c.label}</span>
                        <span className="usage-card-sub">{c.sub}</span>
                    </div>
                ))}
            </div>

            {/* Controls */}
            <div className="usage-controls">
                <div className="usage-range">
                    <label>ช่วง:</label>
                    {[7, 14, 30].map(d => (
                        <button key={d} className={`range-btn ${days === d ? 'active' : ''}`} onClick={() => setDays(d)}>
                            {d} วัน
                        </button>
                    ))}
                </div>
                <button className="steam-btn steam-btn-secondary" onClick={sendLineTest} disabled={sendingLine}>
                    {sendingLine ? 'กำลังส่ง...' : '📤 ทดสอบส่งสรุปเข้า Line'}
                </button>
            </div>

            <div className="usage-line-note">
                ℹ️ ระบบจะส่งสรุปการใช้งานเข้ากลุ่ม Line อัตโนมัติทุกวันเวลา <strong>18:00 น.</strong>
                (ต้องตั้งค่า MOPH Line Notify และเปิดใช้งานในหน้าตั้งค่า)
            </div>

            {/* Daily table */}
            {loading ? (
                <div className="flex-center" style={{ padding: 40 }}><div className="spinner"></div></div>
            ) : (
                <div className="steam-card">
                    <div className="steam-card-header">สรุปรายวัน ({data?.range?.days || days} วันล่าสุด)</div>
                    <div className="steam-card-body" style={{ padding: 0, overflowX: 'auto' }}>
                        <table className="usage-table">
                            <thead>
                                <tr>
                                    <th>วันที่</th>
                                    <th className="num">ผู้ใช้</th>
                                    <th className="num">เข้าระบบ</th>
                                    <th className="num">สลิป</th>
                                    <th className="num">50 ทวิ</th>
                                    <th className="num">อีเมล</th>
                                    <th className="num">แชร์สลิป</th>
                                    <th className="num">คำขอ</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {(data?.daily || []).length === 0 ? (
                                    <tr><td colSpan="9" style={{ textAlign: 'center', padding: 24, color: '#9ca3af' }}>ยังไม่มีข้อมูล</td></tr>
                                ) : data.daily.map(row => (
                                    <tr key={row.date}>
                                        <td>{thaiDate(row.date)}</td>
                                        <td className="num">{fmt(row.unique_users)}</td>
                                        <td className="num">{fmt(row.logins)}</td>
                                        <td className="num">{fmt(row.slip_opens)}</td>
                                        <td className="num">{fmt(row.tawi_opens)}</td>
                                        <td className="num">{fmt(row.emails)}</td>
                                        <td className="num">{fmt(row.slip_shares)}</td>
                                        <td className="num">{fmt(row.requests)}</td>
                                        <td>
                                            <button className="detail-link" onClick={() => viewDetail(row.date)}>ดูรายชื่อ</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Detail modal */}
            {detailDate && (
                <div className="usage-detail-overlay" onClick={() => setDetailDate(null)}>
                    <div className="usage-detail-modal" onClick={e => e.stopPropagation()}>
                        <button className="usage-detail-close" onClick={() => setDetailDate(null)}>&times;</button>
                        <h3>ผู้เข้าใช้ระบบ — {thaiDate(detailDate)}</h3>
                        {detail === null ? (
                            <div className="flex-center" style={{ padding: 24 }}><div className="spinner"></div></div>
                        ) : detail.length === 0 ? (
                            <p style={{ color: '#9ca3af', textAlign: 'center', padding: 20 }}>ไม่มีผู้เข้าใช้ระบบในวันนี้</p>
                        ) : (
                            <table className="usage-table">
                                <thead>
                                    <tr><th>ชื่อ</th><th className="num">เข้าระบบ</th><th className="num">สลิป</th><th className="num">50ทวิ</th><th className="num">อีเมล</th><th className="num">แชร์</th></tr>
                                </thead>
                                <tbody>
                                    {detail.map((u, i) => (
                                        <tr key={i}>
                                            <td>{u.fullname || u.username}</td>
                                            <td className="num">{fmt(u.logins)}</td>
                                            <td className="num">{fmt(u.slip_opens)}</td>
                                            <td className="num">{fmt(u.tawi_opens)}</td>
                                            <td className="num">{fmt(u.emails)}</td>
                                            <td className="num">{fmt(u.slip_shares)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminUsageReport;
